import React from 'react';
import {Card as AntCard, Typography} from 'antd';
import styled, {keyframes} from 'styled-components';
import {ArrowRightOutlined} from '@ant-design/icons';

const {Title, Paragraph} = Typography;

// Animations
const fadeInUp = keyframes`
  from {
    opacity: 0;
    transform: translateY(30px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
`;

const shimmer = keyframes`
  0% {
    background-position: -200% 0;
  }
  100% {
    background-position: 200% 0;
  }
`;

// Utility functions
const tagToText = node => {
  let tag = document.createElement('div');
  tag.innerHTML = node;
  node = tag.innerText;
  return node;
};

const shortenText = (text, startingPoint, maxLength) => {
  return text.length > maxLength ? text.slice(startingPoint, maxLength) : text;
};

const formatDate = dateString => {
  const date = new Date(dateString);
  return date.toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
  });
};

// Styled Components
const BlogCard = styled.div`
  position: relative;
  height: 400px;
  border-radius: ${props => props.theme.borderRadius?.['2xl'] || '1rem'};
  overflow: hidden;
  cursor: pointer;
  transition: all ${props => props.theme.animation?.duration?.normal || '300ms'}
    ${props => props.theme.animation?.easing?.ease || 'cubic-bezier(0.4, 0, 0.2, 1)'};
  animation: ${fadeInUp} 1s ease-out;
  animation-delay: ${props => props.delay || '0s'};
  animation-fill-mode: both;
  box-shadow: ${props =>
    props.theme.boxShadow?.lg ||
    '0 10px 15px -3px rgba(0, 0, 0, 0.1), 0 4px 6px -2px rgba(0, 0, 0, 0.05)'};

  &:hover {
    transform: translateY(-8px);
    box-shadow: ${props =>
      props.theme.boxShadow?.xl ||
      '0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)'};

    .blog-image {
      transform: scale(1.05);
    }

    .blog-overlay {
      background: linear-gradient(
        to top,
        rgba(0, 0, 0, 0.9) 0%,
        rgba(0, 0, 0, 0.6) 50%,
        rgba(0, 0, 0, 0.3) 100%
      );
    }

    .read-more-icon {
      transform: translateX(5px);
    }
  }

  @media (max-width: ${props => props.theme.breakpoints?.md || '768px'}) {
    height: 350px;
  }
`;

const BlogImage = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-image: url(${props => props.image});
  background-size: cover;
  background-position: center;
  background-repeat: no-repeat;
  transition: transform ${props => props.theme.animation?.duration?.normal || '300ms'}
    ${props => props.theme.animation?.easing?.ease || 'cubic-bezier(0.4, 0, 0.2, 1)'};
`;

const BlogOverlay = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: linear-gradient(
    to top,
    rgba(0, 0, 0, 0.8) 0%,
    rgba(0, 0, 0, 0.4) 50%,
    rgba(0, 0, 0, 0.1) 100%
  );
  transition: background ${props => props.theme.animation?.duration?.normal || '300ms'}
    ${props => props.theme.animation?.easing?.ease || 'cubic-bezier(0.4, 0, 0.2, 1)'};
`;

const BlogContent = styled.div`
  position: absolute;
  bottom: 0;
  left: 0;
  right: 0;
  padding: ${props => props.theme.spacing?.[6] || '1.5rem'};
  color: white;
  z-index: 2;
`;

const BlogTitle = styled(Title)`
  && {
    font-family: ${props =>
      props.theme.typography?.fontFamily?.heading ||
      "'Hanson Bold', 'Inter', system-ui, sans-serif"};
    font-size: ${props => props.theme.typography?.fontSize?.lg || '1.125rem'};
    font-weight: ${props => props.theme.typography?.fontWeight?.bold || '700'};
    color: white;
    margin-bottom: ${props => props.theme.spacing?.[2] || '0.5rem'};
    line-height: ${props => props.theme.typography?.lineHeight?.tight || '1.25'};
    display: -webkit-box;
    -webkit-line-clamp: 2;
    -webkit-box-orient: vertical;
    overflow: hidden;

    @media (max-width: ${props => props.theme.breakpoints?.md || '768px'}) {
      font-size: ${props => props.theme.typography?.fontSize?.base || '1rem'};
    }
  }
`;

const BlogMeta = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: ${props => props.theme.spacing?.[3] || '0.75rem'};
  font-size: ${props => props.theme.typography?.fontSize?.sm || '0.875rem'};
  color: rgba(255, 255, 255, 0.8);
`;

const BlogDate = styled.span`
  font-weight: ${props => props.theme.typography?.fontWeight?.medium || '500'};
`;

const ReadMoreLink = styled.div`
  display: flex;
  align-items: center;
  gap: ${props => props.theme.spacing?.[2] || '0.5rem'};
  font-size: ${props => props.theme.typography?.fontSize?.sm || '0.875rem'};
  font-weight: ${props => props.theme.typography?.fontWeight?.semibold || '600'};
  color: ${props => props.theme.colors?.secondary?.[400] || '#fb923c'};
  margin-top: ${props => props.theme.spacing?.[3] || '0.75rem'};

  .read-more-icon {
    transition: transform ${props => props.theme.animation?.duration?.normal || '300ms'}
      ${props => props.theme.animation?.easing?.ease || 'cubic-bezier(0.4, 0, 0.2, 1)'};
  }
`;

const BlogDescription = styled(Paragraph)`
  && {
    color: rgba(255, 255, 255, 0.9);
    font-size: ${props => props.theme.typography?.fontSize?.sm || '0.875rem'};
    line-height: ${props => props.theme.typography?.lineHeight?.relaxed || '1.625'};
    margin-bottom: 0;
    display: -webkit-box;
    -webkit-line-clamp: 2;
    -webkit-box-orient: vertical;
    overflow: hidden;
  }
`;

function BlogPost({post, index = 0}) {
  const handleClick = () => {
    window.open(post.link, '_blank', 'noopener,noreferrer');
  };

  const animationDelay = `${index * 0.1 + 0.2}s`;

  // Extract description from content
  const description = post.description || tagToText(post.content || '');
  const shortDescription =
    shortenText(description, 0, 120) + (description.length > 120 ? '...' : '');

  // Better image fallback logic
  const getImageUrl = () => {
    if (post.thumbnail && post.thumbnail !== '') {
      return post.thumbnail;
    }

    // Try to extract image from content
    if (post.content) {
      const imgMatch = post.content.match(/<img[^>]+src="([^">]+)"/);
      if (imgMatch && imgMatch[1]) {
        return imgMatch[1];
      }
    }

    // Use a gradient background as fallback
    return `data:image/svg+xml;base64,${btoa(`
      <svg width="400" height="300" xmlns="http://www.w3.org/2000/svg">
        <defs>
          <linearGradient id="grad" x1="0%" y1="0%" x2="100%" y2="100%">
            <stop offset="0%" style="stop-color:#e22784;stop-opacity:1" />
            <stop offset="100%" style="stop-color:#fc673d;stop-opacity:1" />
          </linearGradient>
        </defs>
        <rect width="400" height="300" fill="url(#grad)" />
        <text x="200" y="150" font-family="Arial, sans-serif" font-size="24" font-weight="bold" text-anchor="middle" fill="white">CLLCTVE</text>
      </svg>
    `)}`;
  };

  return (
    <BlogCard onClick={handleClick} delay={animationDelay}>
      <BlogImage className="blog-image" image={getImageUrl()} />
      <BlogOverlay className="blog-overlay" />
      <BlogContent>
        <BlogMeta>
          <BlogDate>{formatDate(post.pubDate)}</BlogDate>
        </BlogMeta>
        <BlogTitle level={4}>{post.title}</BlogTitle>
        {shortDescription && <BlogDescription>{shortDescription}</BlogDescription>}
        <ReadMoreLink>
          <span>Read More</span>
          <ArrowRightOutlined className="read-more-icon" />
        </ReadMoreLink>
      </BlogContent>
    </BlogCard>
  );
}

export default BlogPost;
